import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ProposalRoutingConstants } from '@modules/order/order.const';

import {
  ContractsTypes,
  GetArchivalContractsList,
  GetContractDetails,
  GetContractsList,
  RemoveContract,
  SetArchivalContractsList,
  SetContractDetails,
  SetContractsList,
} from '@modules/order/store/contracts/contracts.action';
import { GetProposalDetails } from '@modules/order/store/proposal/proposal.action';
import { ModalReasonComponent } from '@modules/shared/components/modalReason/modalReason.component';
import { AppState } from '@modules/shared/store';
import { HideSpinnerLoading } from '@modules/shared/store/common.actions';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Action, Store } from '@ngrx/store';

import { map, switchMap, tap, filter } from 'rxjs/operators';
import { PlatformService } from '@modules/shared/services/platform.service';
import { ContractDetailsResources } from '@resources/ru/orderModule/contracts/contractDetails/contractDetails.resources';
import { ModalInformationMessageComponent } from '@modules/shared/components/modalInformationMessage/modalInformationMessage.component';
import { Observable, timer } from 'rxjs';
import { CommonResources } from '@resources/ru/commonModule/common.resources';
import { MatDialog } from '@angular/material/dialog';
import { GetContractListResponse } from '@codegen';
import { DefaultService } from '@codegen/service';
import { ContractsService } from '@modules/order/services/contracts.service';

@Injectable()
export class ContractsEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<AppState>>(Store);
  private contractsResourceService = inject(DefaultService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private platformService = inject(PlatformService);
  private contractService = inject(ContractsService);

  public GetContractsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetContractsList>(ContractsTypes.GetContractsList),
      switchMap(() =>
        this.contractsResourceService.contractsFilter({}).pipe(
          switchMap(response => {
            return [
              new SetContractsList(response.contracts),
              new HideSpinnerLoading(),
            ];
          })
        )
      )
    )
  );

  public GetContractDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetContractDetails>(ContractsTypes.GetContractDetails),
      map(data => data.payload),
      switchMap((data): Observable<SetContractDetails> | Action[] => {
        if (!data) {
          this.router.navigateByUrl('/order/contracts/contractsList');
          return [];
        }
        return this.contractsResourceService
          .contractsGetById({ contractId: data })
          .pipe(
            switchMap(response => {
              // this.store.dispatch(new GetProducts(response.contract.number));
              this.store.dispatch(
                new GetProposalDetails(response.contract.applicationId)
              );
              return [new SetContractDetails(response.contract)];
            })
          );
      })
    )
  );

  public GetArchivalContractsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetArchivalContractsList>(ContractsTypes.GetArchivalContractsList),
      switchMap(() =>
        this.contractsResourceService
          .contractsFilter({ statusGroup: 'ARCHIVE' })
          .pipe(
            switchMap((response: GetContractListResponse) => {
              return [
                new SetArchivalContractsList(response.contracts),
                new HideSpinnerLoading(),
              ];
            })
          )
      )
    )
  );

  public RemoveContract$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RemoveContract>(ContractsTypes.RemoveContract),
      map(data => data.payload),
      switchMap(contractId =>
        this.contractsResourceService.contractsCancel({ contractId }).pipe(
          switchMap(() => {
            return this.contractService.getReasonDeleteContract().pipe(
              switchMap(reasons => {
                const dialogOptions = {
                  width: this.platformService.dialogWidth,
                  minWidth: '596px',
                  height: '549px',
                };

                const dialogRef = this.dialog.open(ModalReasonComponent, {
                  ...dialogOptions,
                  data: {
                    reasons,
                    title: this.сontractDetailsResources.reasonForDeleting,
                  },
                });

                return dialogRef.afterClosed().pipe(
                  tap(reason => {
                    if (!reason) {
                      this.router.navigate([
                        `/order/${ProposalRoutingConstants.proposalsList}`,
                      ]);
                      window.scrollTo({ top: 0, left: 0 });
                    }
                  }),
                  filter(reason => reason !== undefined),
                  switchMap(reasons => {
                    const comment =
                      reasons.comment !== undefined
                        ? reasons.comment
                        : undefined;

                    return this.contractsResourceService.contractsCancelReason({
                      contractId: contractId,
                      rejectedReasonIdList: reasons.rejectedReasonIdList,
                      comment: comment,
                    });
                  }),
                  switchMap(() => {
                    const dialogRef = this.dialog.open(
                      ModalInformationMessageComponent,
                      {
                        autoFocus: false,
                        data: {
                          text: this.commonResources.thanksMessage,
                          icon: '/assets/heart.svg',
                        },
                      }
                    );

                    return timer(this.closeTimeout).pipe(
                      tap(() => dialogRef.close()),
                      switchMap((): Action[] => {
                        this.router.navigate([
                          `/order/${ProposalRoutingConstants.proposalsList}`,
                        ]);
                        window.scrollTo({ top: 0, left: 0 });
                        return [];
                      })
                    );
                  })
                );
              })
            );
          })
        )
      )
    )
  );

  public сontractDetailsResources: ContractDetailsResources =
    new ContractDetailsResources();
  public commonResources: CommonResources = new CommonResources();

  private closeTimeout = 3000;
}
